import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Scale } from "lucide-react";


const LegalNoticePage = () => {
  const { t } = useTranslation();

  useSEO({
    title: t("legalPage.seoTitle", "Legal Notice | System Atlas"),
    description: t("legalPage.seoDescription", "Legal notice for System Atlas: publisher identity, hosting, intellectual property and responsibilities."),
    noIndex: false,
  });

  return (
    <>
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 text-sm">
          <ArrowLeft className="w-4 h-4" />
          {t("legalPage.backToHome", "Back to home")}
        </Link>

        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6 tracking-tight">
            {t("legalPage.title", "Legal Notice")}
          </h1>

          <p className="text-sm text-muted-foreground mb-10">
            {t("legalPage.lastUpdated", "Last updated: February 16, 2026")}
          </p>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" />
              {t("legalPage.publisherTitle", "Site Publisher")}
            </h2>
            <div className="text-muted-foreground leading-relaxed space-y-2">
              <p><strong>{t("legalPage.siteName", "Site name:")}</strong> System Atlas</p>
              <p><strong>{t("legalPage.url", "URL:")}</strong> https://systematlas.fr</p>
              <p><strong>{t("legalPage.publisher", "Publisher:")}</strong> {t("legalPage.publisherValue", "The System Atlas team")}</p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">{t("legalPage.hostingTitle", "Hosting")}</h2>
            <div className="text-muted-foreground leading-relaxed space-y-2">
              <p>{t("legalPage.hostingDescription", "System Atlas is hosted by:")}</p>
              <p><strong>Lovable (GPT Engineer Inc.)</strong></p>
              <p>{t("legalPage.hostingSecurity", "Secure cloud infrastructure with data encryption.")}</p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">{t("legalPage.ipTitle", "Intellectual Property")}</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              {t("legalPage.ipContent1", "All content on System Atlas (text, graphics, software, images, videos, sounds, logos, trademarks, etc.) is protected by intellectual property laws.")}
            </p>
            <p className="text-muted-foreground leading-relaxed">
              {t("legalPage.ipContent2", "Any reproduction, representation, modification, publication, distribution or exploitation of the site's content is strictly prohibited without prior written authorization.")}
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">{t("legalPage.liabilityTitle", "Liability")}</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              {t("legalPage.liabilityContent1", "System Atlas endeavors to provide accurate information. However, the site cannot be held responsible for omissions, inaccuracies or shortcomings in updates.")}
            </p>
            <p className="text-muted-foreground leading-relaxed">
              {t("legalPage.liabilityContent2", "Content generated by artificial intelligence is provided for informational purposes and does not substitute professional instruction.")}
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">{t("legalPage.linksTitle", "Links to other pages")}</h2>
            <div className="flex flex-col gap-2 text-sm">
              <Link to="/privacy" className="text-primary hover:underline">
                {t("legalPage.privacyLink", "Privacy Policy")}
              </Link>
              <Link to="/terms" className="text-primary hover:underline">
                {t("legalPage.termsLink", "Terms of Service")}
              </Link>
            </div>
          </section>
        </article>
      </div>
    </>
  );
};

export default LegalNoticePage;
